//import { auth, firestore } from "containers/firebase";
import { COLLECTION } from "shared/strings/firebase";
import { head } from "utils";
import { firestoreToArray, getFirestoreContentWhereFieldInArray } from "./helpers";
import firebase from "firebase/compat/app";
import {
  getAggregateFromServer, sum, count, getFirestore,
  deleteDoc,
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  arrayRemove,
  arrayUnion,
  collectionGroup,
  or,
  and
} from "firebase/firestore";
import { TRANSACTION_TYPES } from "./users";
import dateUtils from "utils/dateUtils";
import { computeGreenpoints } from "utils/greenpoints";
import axios from "axios";
import batchUtils from "utils/batchUtils";
import _ from "lodash";
import { PRODUCT_STATUSES, TENANTS_STATUSES } from "constants/statuses";
import { fi } from "date-fns/locale";
import { generateFirestoreId } from "./common";
import CategoryViewModel from "src/containers/Categories/components/CategoryViewModel";


const VITE_CLOUD_FUNCTION_API_URL = import.meta.env.VITE_CLOUD_FUNCTION_API_URL;
const VITE_FIREBASE_PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;

const apiUrl = VITE_FIREBASE_PROJECT_ID == "greenplay-test-d85fa" ? `https://qr-code-scan-result-test.web.app/validation` : VITE_FIREBASE_PROJECT_ID == "greenplaysherbrooke" ? `https://qr-code-scan-result-sherbrooke.web.app/validation` : `https://qr-code-scan-result.web.app/validation`;

const firestore = getFirestore();
const auth = firebase.auth();

const buildTitleArrayToken = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  const normalized = value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();

  if (!normalized) {
    return "";
  }

  return normalized.split(/\s+/).filter(Boolean)[0] || "";
};

export const prepareTenantProductCategoryToData = (category) => {
  const preparedCategory = !category
    ? {}
    : {
      id: category.id || "",
      categoryName: category.categoryName || '',
      logoUrl: category.logoUrl || '',
      description: category.description || '',
    };
  return preparedCategory;
};

const normalizeImportedUniqueCodes = (codes = []) => {
  if (!Array.isArray(codes)) {
    return [];
  }

  return codes.map((code) => String(code ?? '').trim()).filter(Boolean);
};

const getUniqueCodeDocumentType = (data = {}) => (data.isUniqueQrcode ? 'qrCode' : 'barCode');

const coerceBarcodeDate = (value) => {
  if (!value) {
    return 0;
  }
  if (typeof value?.toDate === 'function') {
    return value.toDate().getTime();
  }
  return new Date(value).getTime() || 0;
};

const isSeriesCodeProduct = (data = {}) =>
  Boolean(data.isUniqueBarcode || data.isUniqueQrcode || data.isUniqueCharacter);

const EMPTY_PRODUCT_STEPS = {
  step1: "",
  step2: "",
  step3: "",
  step4: "",
  step5: "",
};

/** Firestore rejects undefined nested values — coerce missing step fields to "". */
const normalizeProductSteps = (steps) => {
  const source = steps && typeof steps === "object" ? steps : {};
  return {
    step1: source.step1 ?? "",
    step2: source.step2 ?? "",
    step3: source.step3 ?? "",
    step4: source.step4 ?? "",
    step5: source.step5 ?? "",
  };
};

export const syncProductSeriesCodes = async (tenantId, productId, data = {}) => {
  if (!isSeriesCodeProduct(data)) {
    return;
  }

  const barCodesCollectionRef = collection(
    firestore,
    COLLECTION.Tenants,
    tenantId,
    COLLECTION.products,
    productId,
    COLLECTION.barCodes
  );
  const snapshot = await getDocs(barCodesCollectionRef);
  const allCodes = firestoreToArray(snapshot);
  const codeType = getUniqueCodeDocumentType(data);
  const importedCodes = normalizeImportedUniqueCodes(data.uniqueBarCodes);

  if (importedCodes.length > 0) {
    await batchUtils.batchLimitParallel({
      firestore,
      items: importedCodes,
      limit: 250,
      onEach: async (barCode, batch) => {
        const docRef = doc(barCodesCollectionRef, barCode);
        const existingData = (await getDoc(docRef)).data();
        batch.set(docRef, {
          createdAt: existingData?.createdAt || new Date(),
          updatedAt: new Date(),
          ...existingData,
          status: existingData?.status || 'active',
          barCode,
          type: codeType,
        }, { merge: true });
      },
    });

    const importedSet = new Set(importedCodes);
    const toCancel = allCodes.filter(
      (item) => item.status === 'active' && item.type === codeType && !importedSet.has(item.barCode)
    );

    if (toCancel.length > 0) {
      await batchUtils.batchLimitParallel({
        firestore,
        items: toCancel,
        limit: 250,
        onEach: async (item, batch) => {
          batch.set(
            doc(barCodesCollectionRef, item.barCode),
            { status: 'cancelled', updatedAt: new Date() },
            { merge: true }
          );
        },
      });
    }

    return;
  }

  if (!data.isUniqueQrcode) {
    return;
  }

  const targetStock = Number(data.stock) || 0;
  const activeCodes = allCodes.filter((code) => code.status === 'active' && code.type === 'qrCode');
  const codesToAdd = Math.max(0, targetStock - activeCodes.length);

  if (codesToAdd > 0) {
    const newCodes = Array.from({ length: codesToAdd }, () => generateFirestoreId());
    await batchUtils.batchLimitParallel({
      firestore,
      items: newCodes,
      limit: 250,
      onEach: async (qrCode, batch) => {
        const docRef = doc(barCodesCollectionRef, qrCode);
        const existingData = (await getDoc(docRef)).data();
        batch.set(docRef, {
          createdAt: existingData?.createdAt || new Date(),
          barCode: qrCode,
          ...(existingData || {}),
          updatedAt: new Date(),
          status: existingData?.status || 'active',
          type: 'qrCode',
        }, { merge: true });
      },
    });
  }

  const refreshedSnapshot = await getDocs(barCodesCollectionRef);
  const refreshedActiveCodes = firestoreToArray(refreshedSnapshot)
    .filter((code) => code.status === 'active' && code.type === 'qrCode');
  const codesToCancelCount = Math.max(0, refreshedActiveCodes.length - targetStock);

  if (codesToCancelCount > 0) {
    const toCancel = refreshedActiveCodes
      .sort((a, b) => coerceBarcodeDate(b.createdAt) - coerceBarcodeDate(a.createdAt))
      .slice(0, codesToCancelCount);

    await batchUtils.batchLimitParallel({
      firestore,
      items: toCancel,
      limit: 250,
      onEach: async (item, batch) => {
        batch.set(
          doc(barCodesCollectionRef, item.barCode),
          { status: 'cancelled', updatedAt: new Date() },
          { merge: true }
        );
      },
    });
  }
};

export const createTenantProduct = async (tenantId, data) => {
  const newDoc = doc(collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products));
  const tenantDoc = doc(collection(firestore, COLLECTION.Tenants), tenantId);

  if (data.steps_en == undefined) {
    data.steps_en = { ...EMPTY_PRODUCT_STEPS };
  }
  const maxTransactions = Number(data.maxTransactionsPerPeriod.value);

  if (!isNaN(maxTransactions) && typeof maxTransactions === 'number') {
    const stock = Number(data?.stock || 0);

    await setDoc(newDoc, {
      id: newDoc.id,
      images: data?.images,
      title_en: data?.title_en || "",
      title_fr: data?.title_fr || "",
      bodyText_en: data?.bodyText_en || "",
      bodyText_fr: data?.bodyText_fr || '',
      isFeatured: data?.isFeatured || false,
      mainOption: data?.mainOption || '',
      codeFormat: data?.codeFormat || '',
      codeType: data?.codeType || '',
      isNew: data?.isNew || false,
      isOnSale: data?.isOnSale || false,
      isDiscovery: data?.isDiscovery || false,
      isAgeRestricted: data?.isAgeRestricted || false,
      sku: data?.sku,
      displayOrder: data?.displayOrder,
      cityId: data?.cityId,
      tenantId: tenantId,
      tenantName: data?.tenantName,
      productUrl: data?.productUrl,
      price: Number(+data?.price),
      greenpoints: computeGreenpoints(data),
      stock: stock,
      initialStock: Number(data?.stock),
      availability: data?.availability,
      barCode: data?.barCode,
      categories: data?.categories,
      status: data?.status,
      createdOn: new Date(),
      updatedOn: new Date(),
      isApproved: false,
      steps: normalizeProductSteps(data?.steps),
      steps_en: normalizeProductSteps(data?.steps_en),
      isCoupon: data?.isCoupon || false,
      isDelivery: data?.isDelivery || false,
      isBarCode: data?.isBarCode || false,
      isCharacter: data?.isCharacter || false,
      isUniqueCharacter: data?.isUniqueCharacter || false,
      isUniqueBarcode: data?.isUniqueBarcode || false,
      isQrCode: data?.isQrCode || false,
      qrCodeUrl: apiUrl,
      isUniqueQrcode: data?.isUniqueQrcode || false,
      isPdf: data?.isPdf || false,
      isUniquePdf: data?.isUniquePdf || false,
      pdfFiles: data?.pdfFiles || [],
      uniqueBarCodes: data?.uniqueBarCodes || [],
      expirationDate: data?.expirationDate || null,
      orderExpirationDate: data?.orderExpirationDate || null,
      maxTransactionsPerPeriod: {
        value: maxTransactions,
        period: data.maxTransactionsPerPeriod.period
      }
    });
  } else {
    console.error("Erreur : La valeur maxTransactionsPerPeriod n'est pas un nombre valide.");
  }

  if (data.isUniquePdf && data.pdfFiles?.length > 0) {
    await batchUtils.batchLimitParallel({
      firestore: firestore,
      items: data.pdfFiles,
      limit: 250,
      onEach: async (pdf, batch) => {
        const docRef = doc(collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products, newDoc.id, COLLECTION.barCodes), pdf.id);
        batch.set(docRef, {
          createdAt: new Date(),
          status: 'active',
          barCode: pdf.id,
          type: 'pdf',
          pdfUrl: pdf.url || '',
          pdfPath: pdf.path || '',
          pdfName: pdf.name || '',
        }, { merge: true });
      },
    });
  }

  if (isSeriesCodeProduct(data)) {
    await syncProductSeriesCodes(tenantId, newDoc.id, {
      ...data,
      uniqueBarCodes: normalizeImportedUniqueCodes(data.uniqueBarCodes),
    });
  }

  await updateDoc(tenantDoc, { productCount: increment(1) });

};

export const editTenantProduct = async (tenantId, productId, data) => {
  const product = doc(collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products), productId);

  if (data.steps_en == undefined) {
    data.steps_en = { ...EMPTY_PRODUCT_STEPS };
  }
  const maxTransactions = Number(data.maxTransactionsPerPeriod.value);

  if (!isNaN(maxTransactions) && typeof maxTransactions === 'number') {


    await updateDoc(product, {
      images: data?.images,
      title_en: data?.title_en,
      title_fr: data?.title_fr,
      bodyText_en: data?.bodyText_en,
      bodyText_fr: data?.bodyText_fr,
      isFeatured: data?.isFeatured || false,
      isNew: data?.isNew || false,
      isOnSale: data?.isOnSale || false,
      isDiscovery: data?.isDiscovery || false,
      isAgeRestricted: data?.isAgeRestricted || false,
      sku: data?.sku,
      displayOrder: data?.displayOrder,
      cityId: data?.cityId,
      tenantId: tenantId,
      tenantName: data?.tenantName,
      productUrl: data?.productUrl || "",
      price: Number(+data?.price),
      greenpoints: computeGreenpoints(data),
      stock: Number(data?.stock),
      initialStock: Number(data?.initialStock ?? data?.stock),
      availability: data?.availability,
      mainOption: data?.mainOption || '',
      codeFormat: data?.codeFormat || '',
      codeType: data?.codeType || '',
      barCode: data?.barCode || "",
      categories: data?.categories,
      status: data?.status,
      steps: normalizeProductSteps(data?.steps),
      steps_en: normalizeProductSteps(data?.steps_en),
      isApproved: data?.isApproved || false,
      isCoupon: data?.isCoupon || false,
      isDelivery: data?.isDelivery || false,
      isBarCode: data?.isBarCode || false,
      isCharacter: data?.isCharacter || false,
      isUniqueCharacter: data?.isUniqueCharacter || false,
      isUniqueBarcode: data?.isUniqueBarcode || false,
      isQrCode: data?.isQrCode || false,
      qrCodeUrl: apiUrl,
      isUniqueQrcode: data?.isUniqueQrcode || false,
      isPdf: data?.isPdf || false,
      isUniquePdf: data?.isUniquePdf || false,
      pdfFiles: data?.pdfFiles || [],
      uniqueBarCodes: data?.uniqueBarCodes || [],
      updatedOn: new Date(),
      expirationDate: data?.expirationDate || null,
      orderExpirationDate: data?.orderExpirationDate || null,
      maxTransactionsPerPeriod: {
        value: maxTransactions,
        period: data.maxTransactionsPerPeriod.period
      }
    });
  }

  if (isSeriesCodeProduct(data)) {
    await syncProductSeriesCodes(tenantId, productId, {
      ...data,
      uniqueBarCodes: normalizeImportedUniqueCodes(data.uniqueBarCodes),
    });
  }

  if (data.isUniquePdf) {
    const pdfFiles = Array.isArray(data.pdfFiles) ? data.pdfFiles : [];
    const currentIds = new Set(pdfFiles.map(pdf => pdf.id));

    await batchUtils.batchLimitParallel({
      firestore: firestore,
      items: pdfFiles,
      limit: 250,
      onEach: async (pdf, batch) => {
        const docRef = doc(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products, productId, COLLECTION.barCodes, pdf.id);
        const existingData = (await getDoc(docRef)).data();
        batch.set(docRef, {
          createdAt: new Date(),
          ...(existingData || {}),
          updatedAt: new Date(),
          status: existingData?.status || 'active',
          barCode: pdf.id,
          type: 'pdf',
          pdfUrl: pdf.url || '',
          pdfPath: pdf.path || '',
          pdfName: pdf.name || '',
        }, { merge: true });
      },
    });

    const barCodesSnapshot = await getDocs(collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products, productId, COLLECTION.barCodes));
    const removedActivePdfDocs = firestoreToArray(barCodesSnapshot)
      .filter(item => item.type === 'pdf' && item.status === 'active' && !currentIds.has(item.barCode));

    if (removedActivePdfDocs.length > 0) {
      await batchUtils.batchLimitParallel({
        firestore: firestore,
        items: removedActivePdfDocs,
        limit: 250,
        onEach: async (item, batch) => {
          const docRef = doc(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products, productId, COLLECTION.barCodes, item.barCode);
          batch.set(docRef, { status: 'cancelled', updatedAt: new Date() }, { merge: true });
        },
      });
    }
  }

};

export async function updateTenantData(tenantId, data) {

  console.log('Updating tenant data for ID:', tenantId, 'with data:', data);
  const tenantDoc = doc(collection(firestore, COLLECTION.Tenants), tenantId);
  await setDoc(tenantDoc, data, { merge: true });
}

export const getTenantProducts = async (tenantId, params = {}) => {
  const {
    orderBy: orderByField = "updatedOn",
    limit: limitInt,
    pageSize,
    startAfterDoc,
    statusFilter,
    nonArchivedOnly,
    isNew,
  } = params;

  const baseConstraints = [];

  if (statusFilter) {
    baseConstraints.push(where('status', '==', statusFilter));
  } else if (nonArchivedOnly) {
    baseConstraints.push(where('status', 'in', [
      PRODUCT_STATUSES.needApproval,
      PRODUCT_STATUSES.active,
      PRODUCT_STATUSES.disabled,
      PRODUCT_STATUSES.deleted,
      PRODUCT_STATUSES.expired,
    ]));
  }

  if (typeof isNew === 'boolean') {
    baseConstraints.push(where('isNew', '==', isNew));
  }

  const countQuery = query(
    collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products),
    ...baseConstraints
  );
  const totalRecordsSnapshot = await getAggregateFromServer(countQuery, {
    totalRecords: count(),
  });

  const constraints = [...baseConstraints];
  if (orderByField) {
    constraints.push(orderBy(orderByField, "desc"));
  }
  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }
  if (pageSize) {
    constraints.push(limit(pageSize));
  } else if (limitInt) {
    constraints.push(limit(limitInt));
  }

  const q = query(
    collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products),
    ...constraints
  );

  const snapshot = await getDocs(q);
  return {
    rows: firestoreToArray(snapshot),
    lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
    totalRecords: totalRecordsSnapshot.data().totalRecords || 0,
  };
};

export const getTenantProductGroupsById = async (productId) => {

  const q = query(collectionGroup(firestore, COLLECTION.products), where('id', '==', productId));
  const res = await getDocs(q);
  return firestoreToArray(res).map((item, idx) => ({ ...item, tenantId: res.docs[idx].ref.parent.parent.id })).shift();
};

export const getTenantProductById = async (productId, tenantId) => {
  if (!tenantId) {
    return getTenantProductGroupsById(productId);
  }

  const productDoc = doc(collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products), productId);
  const docSnap = await getDoc(productDoc);
  return { ...docSnap.data(), id: docSnap.id };
};

// helpers/tenantBarcodes.js

export const getTenantProductBarcodesForCity = async (productId) => {
  const productsSnapshot = await getDocs(
    query(collectionGroup(firestore, COLLECTION.products),
      where("id", "==", productId))
  );
  const product = productsSnapshot.docs[0];
  if (!product) return [];
  const barcodesSnapshot = await getDocs(query(collection(product.ref, COLLECTION.barCodes)));
  return firestoreToArray(barcodesSnapshot).map(i => ({
    ...i,
    tenantId: product.ref.parent.parent.id,
    isUniqueQrcode: product.data().isUniqueQrcode
  }));
};

export const getTenantProductBarcodes = async (productId, tenantId) => {
  if (!tenantId) {
    return getTenantProductBarcodesForCity(productId);
  }
  const barcodesCollection = query(collection(
    firestore,
    COLLECTION.Tenants,
    tenantId,
    COLLECTION.products,
    productId,
    COLLECTION.barCodes
  ));
  const snapshot = await getDocs(barcodesCollection);
  return firestoreToArray(snapshot);
};

export const updateTenantProductBarCode = async (productId, tenantId, barCode, data) => {
  const barCodeDoc = doc(
    collection(
      firestore,
      COLLECTION.Tenants,
      tenantId,
      COLLECTION.products,
      productId,
      COLLECTION.barCodes
    ),
    barCode
  );

  await updateDoc(barCodeDoc, data);

  const normalizedBarCode = typeof barCode === 'string' ? barCode.trim() : barCode;
  const productDoc = doc(
    firestore,
    COLLECTION.Tenants,
    tenantId,
    COLLECTION.products,
    productId
  );

  // Keep product-level uniqueBarCodes in sync with barcode availability.
  if (data?.status === 'cancelled' || data?.status === 'used') {
    await updateDoc(productDoc, {
      uniqueBarCodes: arrayRemove(normalizedBarCode),
      updatedOn: new Date(),
    });
  }

  if (data?.status === 'active') {
    await updateDoc(productDoc, {
      uniqueBarCodes: arrayUnion(normalizedBarCode),
      updatedOn: new Date(),
    });
  }
};

export const getOrderIdFromOrderNumber = async (orderNumber, tenantId) => {
  if (!orderNumber || !tenantId) {
    return null;
  }

  const ordersQuery = query(
    collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.orders),
    where('orderNumber', '==', orderNumber),
    limit(1)
  );

  const querySnapshot = await getDocs(ordersQuery);
  if (querySnapshot.empty) {
    return null;
  }

  return querySnapshot.docs[0].id;
};

export const getTenantProductsForCity = async (params = {}) => {
  const {
    limit: limitInt,
    cityId,
    pageSize,
    startAfterDoc,
    statusFilter,
    nonArchivedOnly,
    isNew,
  } = params;
  const baseConstraints = [];

  if (cityId) {
    baseConstraints.push(where('cityId', '==', cityId));
  }

  if (statusFilter) {
    baseConstraints.push(where('status', '==', statusFilter));
  } else if (nonArchivedOnly) {
    baseConstraints.push(where('status', 'in', [
      PRODUCT_STATUSES.needApproval,
      PRODUCT_STATUSES.active,
      PRODUCT_STATUSES.disabled,
      PRODUCT_STATUSES.deleted,
      PRODUCT_STATUSES.expired,
    ]));
  }

  if (typeof isNew === 'boolean') {
    baseConstraints.push(where('isNew', '==', isNew));
  }

  const countQuery = query(collectionGroup(firestore, COLLECTION.products), ...baseConstraints);
  const totalRecordsSnapshot = await getAggregateFromServer(countQuery, {
    totalRecords: count(),
  });

  const constraints = [...baseConstraints];

  constraints.push(orderBy('updatedOn', 'desc'));

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  if (pageSize) {
    constraints.push(limit(pageSize));
  } else if (limitInt) {
    constraints.push(limit(limitInt));
  }

  const q = query(collectionGroup(firestore, COLLECTION.products), ...constraints);

  const snapshot = await getDocs(q);
  const results = firestoreToArray(snapshot);
  results.sort((a, b) => new Date(b.updatedOn?.toDate()) - new Date(a.updatedOn?.toDate()));

  return {
    rows: results,
    lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
    totalRecords: totalRecordsSnapshot.data().totalRecords || 0,
  };
};

export const updateTenantProductFields = async (productId, payload) => {
  const { docs } = await getDocs(query(
    collectionGroup(firestore, COLLECTION.products),
    where('id', '==', productId)
  ));

  if (docs.size < 1) {
    throw new Error(`Product ${productId} doesn't exists`)
  }

  const productToChange = head(docs);
  const resolvedTenantId = payload.tenantId || productToChange?.ref?.parent?.parent?.id;
  if (!resolvedTenantId) {
    throw new Error(`updateTenantProductFields: tenantId missing for product ${productId}`);
  }

  // Ensure downstream calls (expireProductOrders) receive tenantId even if caller omitted it
  if (!payload.tenantId) {
    payload.tenantId = resolvedTenantId;
  }

  if (isSeriesCodeProduct(payload)) {
    await syncProductSeriesCodes(payload.tenantId, productId, {
      ...payload,
      uniqueBarCodes: normalizeImportedUniqueCodes(payload.uniqueBarCodes),
    });
  }
  if (payload.isUniquePdf) {
    const pdfFiles = Array.isArray(payload.pdfFiles) ? payload.pdfFiles : [];
    const currentIds = new Set(pdfFiles.map(pdf => pdf.id));

    await batchUtils.batchLimitParallel({
      firestore: firestore,
      items: pdfFiles,
      limit: 250,
      onEach: async (pdf, batch) => {
        const docRef = doc(firestore, COLLECTION.Tenants, payload.tenantId, COLLECTION.products, productId, COLLECTION.barCodes, pdf.id);
        const data = (await getDoc(docRef)).data();
        batch.set(docRef, {
          createdAt: new Date(),
          ...(data || {}),
          updatedAt: new Date(),
          status: data?.status || 'active',
          barCode: pdf.id,
          type: 'pdf',
          pdfUrl: pdf.url || '',
          pdfPath: pdf.path || '',
          pdfName: pdf.name || '',
        }, { merge: true });
      },
    });

    const barCodesSnapshot = await getDocs(collection(firestore, COLLECTION.Tenants, payload.tenantId, COLLECTION.products, productId, COLLECTION.barCodes));
    const removedActivePdfDocs = firestoreToArray(barCodesSnapshot)
      .filter(item => item.type === 'pdf' && item.status === 'active' && !currentIds.has(item.barCode));

    if (removedActivePdfDocs.length > 0) {
      await batchUtils.batchLimitParallel({
        firestore: firestore,
        items: removedActivePdfDocs,
        limit: 250,
        onEach: async (item, batch) => {
          const docRef = doc(firestore, COLLECTION.Tenants, payload.tenantId, COLLECTION.products, productId, COLLECTION.barCodes, item.barCode);
          batch.set(docRef, { status: 'cancelled', updatedAt: new Date() }, { merge: true });
        },
      });
    }
  }

  if (payload.maxTransactionsPerPeriod) {
    payload.maxTransactionsPerPeriod = {
      value: Number(payload.maxTransactionsPerPeriod.value),
      period: payload.maxTransactionsPerPeriod.period
    };
  }

  if (payload.steps !== undefined) {
    payload.steps = normalizeProductSteps(payload.steps);
  }
  if (payload.steps_en !== undefined) {
    payload.steps_en = normalizeProductSteps(payload.steps_en);
  }

  return updateDoc(productToChange.ref, {
    ..._.omitBy(
      payload, _.isUndefined),
    updatedOn: new Date(),
  });
};


export const getTenantOrdersSums = async (user, params = {}) => {
  const { tenantId, cityId } = user;

  if (!tenantId) {

    return getTenantOrdersSumsForCity(cityId, params);
  }

};

const buildOrderFilterConstraints = ({ logType, startDate, filters = {} }) => {
  const constraints = [];

  if (logType && startDate) {
    const toDate = dateUtils.getEndDateByLogTypeAndStartDate(logType, startDate);
    constraints.push(
      where('createdAt', '>=', dateUtils.convertDateToFirestoreTimestamp(startDate)),
      where('createdAt', '<=', dateUtils.convertDateToFirestoreTimestamp(toDate))
    );
  }

  if (filters.startDate) {
    constraints.push(
      where('createdAt', '>=', dateUtils.convertDateToFirestoreTimestamp(filters.startDate))
    );
  }

  if (filters.endDate) {
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);
    constraints.push(
      where('createdAt', '<=', dateUtils.convertDateToFirestoreTimestamp(endDate))
    );
  }

  if (filters.status && filters.status !== 'all') {
    constraints.push(where('status', '==', filters.status));
  }

  const productNameToken = buildTitleArrayToken(filters.productName);
  if (productNameToken) {
    constraints.push(where('transactionDetails.titleArray', 'array-contains', productNameToken));
  }

  if (filters.tenantId && filters.tenantId.trim()) {
    constraints.push(where('transactionDetails.tenantId', '==', filters.tenantId));
  }

  if (filters.tenantName && filters.tenantName.trim()) {
    constraints.push(where('transactionDetails.tenantName', '==', filters.tenantName));
  }

  if (filters.productTypes && filters.productTypes.length > 0) {
    filters.productTypes.forEach((type) => {
      constraints.push(where(`transactionDetails.feature.${type}`, '==', true));
    });
  }

  return constraints;
};

const fetchTenantOrdersPage = async ({
  collectionRef,
  filterConstraints,
  orderByField = 'createdAt',
  orderDirection = 'desc',
  limitInt,
  pageSize,
  startAfterDoc,
  startAfterValue,
}) => {
  const resolvedOrderDirection = orderDirection.toLowerCase() === 'asc' ? 'asc' : 'desc';
  const resolvedStartAfter = startAfterDoc ?? startAfterValue ?? null;
  const shouldPaginate = !!pageSize || !!limitInt;

  const countQueryArgs = [collectionRef];
  if (filterConstraints.length > 0) {
    countQueryArgs.push(and(...filterConstraints));
  }

  const countQuery = query(...countQueryArgs);
  const totalRecordsSnapshot = await getAggregateFromServer(countQuery, {
    totalRecords: count(),
  });

  const dataQueryArgs = [collectionRef];
  if (filterConstraints.length > 0) {
    dataQueryArgs.push(and(...filterConstraints));
  }

  if (orderByField) {
    dataQueryArgs.push(orderBy(orderByField, resolvedOrderDirection));
  }

  if (resolvedStartAfter) {
    dataQueryArgs.push(startAfter(resolvedStartAfter));
  }

  if (pageSize) {
    dataQueryArgs.push(limit(pageSize));
  } else if (limitInt) {
    dataQueryArgs.push(limit(limitInt));
  }

  const snapshot = await getDocs(query(...dataQueryArgs));

  if (!shouldPaginate) {
    return firestoreToArray(snapshot);
  }

  return {
    rows: firestoreToArray(snapshot),
    lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
    totalRecords: totalRecordsSnapshot.data().totalRecords || 0,
  };
};

export const getTenantOrders = async (user, params = {}) => {
  const { tenantId, cityId } = user;
  if (!tenantId) {
    return getTenantOrdersForCity(cityId, params);
  }

  const {
    orderBy: orderByField = "createdAt",
    sortBy,
    sortByDirection,
    orderDirection,
    sortOrder,
    limit: limitInt,
    logType,
    startDate,
    pageSize,
    startAfterDoc,
    startAfter: startAfterVal,
    startAfterValue,
    filters = {}
  } = params;

  const resolvedOrderByField = sortBy || orderByField || "createdAt";
  const resolvedOrderDirection = (sortByDirection || orderDirection || sortOrder || "desc").toLowerCase() === "asc" ? "asc" : "desc";
  const resolvedStartAfter = typeof startAfterDoc !== 'undefined'
    ? startAfterDoc
    : (typeof startAfterVal !== 'undefined' ? startAfterVal : startAfterValue);

  const filterConstraints = buildOrderFilterConstraints({ logType, startDate, filters });
  const collectionRef = collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.orders);

  try {
    return await fetchTenantOrdersPage({
      collectionRef,
      filterConstraints,
      orderByField: resolvedOrderByField,
      orderDirection: resolvedOrderDirection,
      limitInt,
      pageSize,
      startAfterDoc: resolvedStartAfter,
    });
  } catch (error) {
    console.log('error', error);
    return pageSize || limitInt ? { rows: [], lastVisible: null, totalRecords: 0 } : [];
  }
};

export const getTenantOrderDetailsById = async (tenantId, orderId) => {
  if (!tenantId) {
    return getTenantOrderGroupsById(orderId)
  }

  return await getDoc(doc(collection(firestore, COLLECTION.Tenants), tenantId).collection(COLLECTION.orders).doc(orderId)).then(order => order.data());
}


export const getTenantOrdersSumsForCity = async (cityId, params = {}) => {
  const { startDate, logType } = params;
  if (!logType || !startDate) return [];

  let toDate = dateUtils.getEndDateByLogTypeAndStartDate(logType, startDate);

  // Génère les sous-périodes à agréger
  let periods = [];
  if (logType === "week") {
    periods = dateUtils.getDaysInRange(startDate, toDate); // array de dates (YYYY-MM-DD)
  } else if (logType === "month") {
    periods = dateUtils.getDaysInRange(startDate, toDate); // array de {start, end}
  } else if (logType === "year") {
    periods = dateUtils.getMonthsInRange(startDate, toDate); // array de {start, end}
  } else {
    periods = dateUtils.getDaysInRange(startDate, toDate);
  }

  // Pour chaque sous-période, lance une agrégation
  const results = [];
  for (const period of periods) {
    let periodStart, periodEnd, label;
    if (typeof period === "string") {
      // Cas jour (YYYY-MM-DD)
      periodStart = new Date(period + "T00:00:00");
      periodEnd = new Date(period + "T23:59:59");
      label = period;
    } else {
      // Cas semaine/mois : {start, end, label}
      periodStart = period.start;
      periodEnd = period.end;
      label = period.label;
    }

    const q = query(
      collectionGroup(firestore, "orders"),
      where("cityId", "==", cityId),
      where("createdAt", ">=", dateUtils.convertDateToFirestoreTimestamp(periodStart)),
      where("createdAt", "<=", dateUtils.convertDateToFirestoreTimestamp(periodEnd))
    );

    const snapshot = await getAggregateFromServer(q, {
      transactionValue: sum("transactionValue"),
      transactionCount: count("transactionValue"),
    });


    results.push({
      period: label,
      transactionValue: snapshot.data().transactionValue || 0,
      transactionCount: snapshot.data().transactionCount || 0,
    });
  }

  return results;
};

export const getTenantOrdersForCity = async (cityId, params = {}) => {
  const {
    orderBy: orderByField = "createdAt",
    sortByDirection = "desc",
    orderDirection,
    limit: limitInt,
    startDate,
    logType,
    pageSize,
    startAfterDoc,
    startAfterValue,
    filters = {}
  } = params;

  const resolvedOrderDirection = (sortByDirection || orderDirection || "desc").toLowerCase() === "asc" ? "asc" : "desc";
  const resolvedStartAfter = typeof startAfterDoc !== 'undefined' ? startAfterDoc : startAfterValue;

  const filterConstraints = [
    where('cityId', '==', cityId),
    ...buildOrderFilterConstraints({ logType, startDate, filters }),
  ];

  try {
    return await fetchTenantOrdersPage({
      collectionRef: collectionGroup(firestore, COLLECTION.orders),
      filterConstraints,
      orderByField,
      orderDirection: resolvedOrderDirection,
      limitInt,
      pageSize,
      startAfterDoc: resolvedStartAfter,
    });
  } catch (error) {
    console.log('error', error);
    return pageSize || limitInt ? { rows: [], lastVisible: null, totalRecords: 0 } : [];
  }
};
export const getTenantOrderGroupsById = orderId => {
  const q = query(collectionGroup(firestore, COLLECTION.orders)
    , where('id', '==', orderId), limit(1));
  return getDocs(q)
    .then(firestoreToArray)
    .then(head)

};

export const getTenantInfoById = async (tenantId) => {
  const tenantDocRef = doc(firestore, COLLECTION.Tenants, tenantId);
  const snap = await getDoc(tenantDocRef);
  return snap.exists() ? { ...snap.data(), id: snap.id } : null;
};
const CITY_TENANT_COUNTER_FIELDS = ["cityId", "name", "orderCount", "productCount", "customerCount"];

const pickTenantCounterFields = (tenant = {}) => {
  const picked = { id: tenant.id };
  CITY_TENANT_COUNTER_FIELDS.forEach((field) => {
    if (tenant[field] !== undefined) {
      picked[field] = tenant[field];
    }
  });
  return picked;
};

export const getConnectedCityTenants = async (cityId) => {
  // Note: Firestore Web client SDK does not support Query.select() (Admin SDK only).
  const tenants = await getDocs(
    query(collection(firestore, COLLECTION.Tenants), where("cityId", "==", cityId))
  ).then(firestoreToArray);

  return tenants.map(pickTenantCounterFields);
};

export const tenantUpdateOrder = async (tenantId, orderId, updateData) => {
  return await updateDoc(
    collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.orders, orderId),
    updateData
  );
};

export const deleteTenantProduct = async (tenantId, productId) => {
  const tenantDocRef = doc(collection(firestore, COLLECTION.Tenants), tenantId);
  const productDocRef = doc(collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products), productId);
  const barCodesCollectionRef = collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.products, productId, COLLECTION.barCodes);

  // Delete all barcodes in batches
  const barCodesSnapshot = await getDocs(barCodesCollectionRef);
  if (!barCodesSnapshot.empty) {
    await batchUtils.batchLimitParallel({
      firestore,
      items: barCodesSnapshot.docs,
      limit: 250,
      onEach: async (barcodeDoc, batch) => {
        batch.delete(barcodeDoc.ref);
      },
    });
  }

  // Delete the product document
  await deleteDoc(productDocRef);

  // Decrement the product count
  await updateDoc(tenantDocRef, { productCount: increment(-1) });

  return true;
}

export const getTenantCategoryOptions = async (tenantId) => {
  return await getDocs(collection(firestore, COLLECTION.Tenants, tenantId, COLLECTION.categories))
    .then(firestoreToArray)
};

export const getTenantTransactionsByTime = async (tenantId, fromTimeForGettingTransactions, toTimeForGettingTransactions) => {
  const constraints = [
    where('createdAt', '>', fromTimeForGettingTransactions),
    where('createdAt', '<', toTimeForGettingTransactions),
    where('transactionType', '==', TRANSACTION_TYPES.SHOPPING),
    where('transactionDetails.tenantId', '==', tenantId)
  ];
  const q = query(collectionGroup(firestore, COLLECTION.orders), ...constraints);
  const snapshot = await getDocs(q);
  return firestoreToArray(snapshot);
};

export const getTenantData = (tenantId) => {
  const tenantCollection = doc(collection(firestore, COLLECTION.Tenants), tenantId);
  return getDoc(tenantCollection)
    .then((snap) => {
      if (snap.exists) {
        return { ...snap.data(), id: snap.id };
      } else {
        return getPendingTenantData(tenantId);
      }
    });
};

export const getPendingTenantData = async (tenantId) => {
  const tenantDoc = doc(collection(firestore, COLLECTION.Tenants), tenantId);
  const snap = await getDoc(tenantDoc);
  return { status: TENANTS_STATUSES.pending, ...snap.data(), id: snap.id, tenantId: snap.id };
};

export const getAllTenants = async () => {
  const snapshot = await getDocs(collection(firestore, COLLECTION.Tenants));
  return firestoreToArray(snapshot);
}

export const sendOrderSentEmail = (orderId, tenantId) => {
  if (!orderId || !tenantId) {
    throw 'orderId and tenantId are required';
  }

  return auth.currentUser.getIdToken().then((token) => {
    axios.post(
      `${VITE_CLOUD_FUNCTION_API_URL}/messagingApi/sendOrderSentEmail`,
      { orderId: orderId, tenantId: tenantId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }).catch((error) => {
    console.log(error);
  });


}

export const expireProductOrders = async (tenantId, productId, date) => {
  if (!tenantId || !productId) {
    const error = new Error('expireProductOrders: tenantId and productId are required');
    console.error(error.message);
    throw error;
  }

  if (!auth.currentUser) {
    const error = new Error('expireProductOrders: User must be authenticated');
    console.error(error.message);
    throw error;
  }

  try {
    const orderExpirationDate = typeof date === "string"
      ? date
      : date instanceof Date
        ? date.toISOString().slice(0, 10)
        : ""
    const token = await auth.currentUser.getIdToken();

    const response = await axios.post(
      `${VITE_CLOUD_FUNCTION_API_URL}/scheduleProductOrderExpiration`,
      { tenantId, productId, orderExpirationDate },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // Send identifiers in query string too, in case body parsing fails on the cloud function
        params: { tenantId, productId, orderExpirationDate },
      }
    );
    console.log('scheduleProductOrderExpiration: Successfully expired orders', response.data);
    return response.data;
  } catch (error) {
    console.error('scheduleProductOrderExpiration: Error expiring orders', error);
    throw error;
  }
}

export const createPendingTenant = async (data) => {

  const user = auth.currentUser;
  if (!user?.uid) {
    throw new Error("createPendingTenant requires an authenticated user");
  }
  const tenantDocRef = doc(collection(firestore, COLLECTION.Tenants));
  const tenantId = tenantDocRef.id;
  const tenantData = {
    ...data,
    id: tenantId,
    tenantId,
    userId: user.uid,
  };

  await setDoc(tenantDocRef, tenantData, { merge: true });

  // Seed default categories under the new tenant document
  const categoryViewModel = new CategoryViewModel();
  const categories = await categoryViewModel.getCategories(data.cityId);
  const categoriesData = Array.isArray(categories)
    ? categories.map((item, index) => ({
      key: index + 1,
      categoryName: item.categoryName,
      description: item.description,
      logoUrl: item.logoUrl,
      categoryId: item.categoryId,
    }))
    : [];

  for (const category of categoriesData) {
    const categoryDocRef = doc(
      firestore,
      COLLECTION.Tenants,
      tenantId,
      COLLECTION.categories,
      category.categoryId
    );
    await setDoc(categoryDocRef, category, { merge: true });
  }

  return tenantId;
}

export const deleteTenant = async (tenantId, status = 'pending') => {
  const col = COLLECTION.Tenants;

  await auth.currentUser.getIdToken().then((token) => {

    return axios.post(
      `${VITE_CLOUD_FUNCTION_API_URL}/app/v2/delete-doc-with-subCollections`,
      { collectionPath: col, docId: tenantId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }).catch((error) => {
    console.log(error);
    console.log(error.message);
  });
  return true;
}

export const moveTenantPendingData = async (pendingTenantId, newTenantId, updateDataForAllSubDocs) => {
  await auth.currentUser.getIdToken().then((token) => {
    return axios.post(
      `${VITE_CLOUD_FUNCTION_API_URL}/app/v2/move-subCollections`,
      {
        fromCollectionPath: COLLECTION.Tenants,
        fromDocId: pendingTenantId,
        toCollectionPath: COLLECTION.Tenants,
        toDocId: newTenantId,
        updateDataNested: updateDataForAllSubDocs,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }).catch((error) => {
    console.log(error);
  });
}

export const createUserTransaction = async ({ productData, userData }) => {
  const titleArrayFr = productData.title_fr
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .split(/\s+/);

  const titleArrayEn = productData.title_en
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .split(/\s+/) || [];

  const firstNameArray = userData.firstName
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .split(/\s+/) || [];

  const lastNameArray = userData.lastName
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .split(/\s+/) || [];

  const titleArray = [...new Set([...titleArrayFr, ...titleArrayEn, firstNameArray, lastNameArray])];
  const transactionData = {
    userId: userData.id,
    orderId: "327853", //TODO: generate valid order id
    greenpoints: 0,
    createdBy: "ADMIN",
    transactionDetails: {
      titleArray: titleArray || [],
      productNameFr: productData.title_fr || "",
      categoryName: productData.category?.categoryName || "",
      sku: productData.sku,
      productId: productData.id,
      categoryId: productData.category?.id || "",
      imageUrl: productData.image || "",
      tenantName: productData.tenantName || "",
      productName: productData.title_en || "",
      tenantId: productData.tenantId,
    },
    price: +productData.price || 0,
  };

  await axios.post(`${VITE_CLOUD_FUNCTION_API_URL}/app/v2/transaction/spend`, transactionData);
};

export const createProductTransactionsByAdmin = async ({ usersEmails, productData }) => {
  const usersCollection = collection(firestore, COLLECTION.Users);

  // Fonction pour rechercher les utilisateurs avec un tableau d'emails donné
  const fetchUsersByEmails = async (emails) => {

    return await getFirestoreContentWhereFieldInArray({
      collectionRef: usersCollection,
      field: "email",
      valuesToSearchIn: emails,
    });
  };

  let usersToCreateOrders = await fetchUsersByEmails(usersEmails);

  if (usersToCreateOrders.length === 0) {

    console.log("Aucun utilisateur trouvé avec les emails fournis.");
    return false;

  }

  // Créer les transactions pour les utilisateurs trouvés
  for (const user of usersToCreateOrders) {
    await createUserTransaction({ productData, userData: user });
  }

  return true;
};

export const getNewMerchantId = async () => {
  try {
    const response = await axios.get(`${VITE_CLOUD_FUNCTION_API_URL}/getNewMerchantId`);
    const { merchantId } = response.data;
    return merchantId;
  } catch (error) {
    console.log(error);
  }
}

export const getAllTenantsFromProductsByCity = async (cityId) => {

  const productsQuery = query(
    collectionGroup(firestore, COLLECTION.products),
    where("cityId", "==", cityId),
  );

  const snapshot = await getDocs(productsQuery);

  // Extract tenantName and remove duplicates
  const tenantNamesMap = new Map();
  snapshot.forEach((doc) => {
    const data = doc.data();
    if (data.tenantName) {
      const normalizedTenantName = data.tenantName.trim().toLowerCase(); // Normalize tenantName
      if (!tenantNamesMap.has(normalizedTenantName)) {
        tenantNamesMap.set(normalizedTenantName, {
          name: data.tenantName.trim(),
        });
      }
    }
  });

  // Convert Map values to Array and sort alphabetically by tenantName
  return Array.from(tenantNamesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};


